import { Button } from "@clockwise/design-system";
import { fg_default } from "@clockwise/design-system/tokens";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { CaretCircleDown, CaretCircleUp } from "phosphor-react";
import React, { useRef } from "react";

const TIME_OPEN = 2500;

type Props = {
  name: string;
  children: React.ReactNode;
  iconClosed?: React.ComponentProps<typeof Button>["startIcon"];
  iconOpen?: React.ComponentProps<typeof Button>["startIcon"];
  size?: React.ComponentProps<typeof Button>["size"];
};

export const DropdownButton = ({
  name,
  children,
  iconClosed: IconClosed,
  iconOpen: IconOpen,
  size,
}: Props) => {
  const [open, setOpen] = React.useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, TIME_OPEN);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleClick = () => {
    setOpen((open) => !open);
  };

  return (
    <div
      className="cw-relative cw-inline-block cw-text-left"
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <div>
        <Button
          variant="text"
          sentiment="neutral"
          onClick={handleClick}
          aria-label={name}
          id={`menu-${name}`}
          startIcon={open ? IconOpen : IconClosed}
          size={size}
        >
          {open
            ? !IconOpen && <CaretCircleUp color={fg_default} fill="none" weight="light" />
            : !IconClosed && <CaretCircleDown color={fg_default} fill="none" weight="light" />}
        </Button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.1 }}
            className={classNames(
              "cw-absolute cw-right-0 cw-mt-2 cw-origin-top-right",
              "cw-z-10",
              "cw-w-max",
              "cw-bg-default",
              "cw-rounded-md",
              "cw-shadow-lg",
              "cw-focus:outline-none",
              "cw-ring-1 cw-ring-default-hover cw-ring-opacity-50",
            )}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby={`menu-${name}`}
            tabIndex={-1}
          >
            <div className="cw-py-1" role="none">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DropdownButton;
